'use client'

import { Checkbox, Divider, InputText, Spacer, Text } from '@vinted/web-ui'
import { useCallback, useEffect } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import {
  BusinessAccountType,
  BusinessRegistrationModel,
} from '@marketplace-web/business-accounts/business-accounts-data'
import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { isValueInObject } from '@marketplace-web/shared/utils'
import { CountryModel } from '@marketplace-web/country/country-data'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { NewBusinessDetailsField } from '../../../../../../constants'
import { camelCase } from '../../../utils'

const TRANSLATION_PREFIX = 'business.registration'

type Props = {
  defaultValues: BusinessRegistrationModel
  apiErrors: Array<ErrorItem> | null
  selectedCountry: CountryModel | undefined
}

const LegalEntitySection = ({ defaultValues, apiErrors = [], selectedCountry }: Props) => {
  const {
    register,
    setValue,
    setError,
    clearErrors,
    control,
    watch,
    reset,
    formState: { errors },
  } = useFormContext<Partial<BusinessRegistrationModel>>()
  const translate = useTranslate(`${TRANSLATION_PREFIX}.business_details`)
  const validationMessage = useFormValidationMessage(errors, TRANSLATION_PREFIX)
  const errorsTranslate = useTranslate('errors')

  const companyNumberValidationsAbTest = useAbTest('b2c_company_number_field_validations')
  const siretNumberDisclaimerAbTest = useAbTest('b2c_siret_number_disclaimers_fr')

  const isCompanyNumberValidationsAbTestExposed = selectedCountry?.isoCode === 'ES'
  const isSiretNumberDisclaimerAbTestExposed = selectedCountry?.isoCode === 'FR'

  const isCompanyNumberValidationsChangesEnabled =
    companyNumberValidationsAbTest?.variant === 'on' && isCompanyNumberValidationsAbTestExposed
  const isSiretNumberDisclaimerEnabled =
    siretNumberDisclaimerAbTest?.variant === 'on' && isSiretNumberDisclaimerAbTestExposed

  useTrackAbTest(companyNumberValidationsAbTest, isCompanyNumberValidationsAbTestExposed)
  useTrackAbTest(siretNumberDisclaimerAbTest, isSiretNumberDisclaimerAbTestExposed)

  const isCountryItaly = selectedCountry?.isoCode === 'IT'
  const shouldUseCountrySpecificMessages =
    selectedCountry?.isoCode === 'IT' ||
    selectedCountry?.isoCode === 'ES' ||
    selectedCountry?.isoCode === 'GB'

  useEffect(() => {
    apiErrors?.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (!isValueInObject(fieldName, NewBusinessDetailsField)) return

      setError(fieldName, {
        type: 'manual',
        message: value,
      })
    })
  }, [setError, apiErrors])

  const entityType = watch(NewBusinessDetailsField.EntityType)
  const isVatDisabled = watch(NewBusinessDetailsField.IsVatDisabled)
  const isUserGBSoleTrader =
    selectedCountry?.isoCode === 'GB' && entityType === BusinessAccountType.SoleTrader

  const isVatFieldHidden =
    selectedCountry?.isoCode === 'PT' ||
    (selectedCountry?.isoCode === 'ES' &&
      (entityType === BusinessAccountType.SoleTrader || isCompanyNumberValidationsChangesEnabled))

  const isVatFieldMandatory =
    isCountryItaly ||
    (selectedCountry?.isoCode === 'ES' && entityType !== BusinessAccountType.SoleTrader)

  const getLegalCodeTitle = () => {
    if (isCompanyNumberValidationsChangesEnabled) return translate('legal_code.title')

    return entityType === BusinessAccountType.SoleTrader
      ? translate('legal_code.title')
      : translate('legal_code.title_organisation')
  }

  const getLegalCodePlaceholder = () =>
    entityType === BusinessAccountType.SoleTrader
      ? translate('legal_code.placeholder')
      : translate('legal_code.placeholder_organisation')

  const getVatTitle = () =>
    entityType === BusinessAccountType.SoleTrader
      ? translate('vat.title')
      : translate('legal_code.title')

  const handleIsVatDisabledChange = useCallback(() => {
    setValue(NewBusinessDetailsField.Vat, '')
  }, [setValue])

  useEffect(() => {
    if (isVatDisabled) {
      handleIsVatDisabledChange()
    }
  }, [handleIsVatDisabledChange, isVatDisabled, setValue])

  useEffect(() => {
    if (!defaultValues.businessDetails || !defaultValues.addresses) return
    reset({ businessDetails: defaultValues.businessDetails, addresses: defaultValues.addresses })
  }, [reset, defaultValues])

  const getGenericFieldErrorMessage = (field: string): Record<string, string> => {
    return {
      required: errorsTranslate('blank', {
        field: translate(`${field}`),
      }),
    }
  }

  function getCountrySpecificLegalCodeErrorMessage(): Record<string, string> {
    return entityType === BusinessAccountType.SoleTrader
      ? getGenericFieldErrorMessage('legal_code.title')
      : getGenericFieldErrorMessage('legal_code.title_organisation')
  }

  function getCountrySpecificVatErrorMessage(): Record<string, string> {
    return entityType === BusinessAccountType.SoleTrader
      ? getGenericFieldErrorMessage('vat.title')
      : getGenericFieldErrorMessage('legal_code.title')
  }

  function getCountrySpecificErrorMessage(
    fieldName: NewBusinessDetailsField,
  ): Record<string, string> | undefined {
    if (shouldUseCountrySpecificMessages && fieldName === NewBusinessDetailsField.LegalCode) {
      return getCountrySpecificLegalCodeErrorMessage()
    }

    if (isCountryItaly && fieldName === NewBusinessDetailsField.Vat) {
      return getCountrySpecificVatErrorMessage()
    }

    return undefined
  }

  function renderErrorMessage(fieldName: NewBusinessDetailsField) {
    const errorMessage = shouldUseCountrySpecificMessages
      ? validationMessage(fieldName, getCountrySpecificErrorMessage(fieldName))
      : validationMessage(fieldName)

    if (errorMessage) {
      return <Text as="h4" type={Text.Type.Caption} text={errorMessage} theme="warning" html />
    }

    return null
  }

  useEffect(() => {
    return () => {
      clearErrors()
    }
  }, [clearErrors])

  return (
    <SeparatedList separator={<Divider />}>
      <InputText
        {...register(NewBusinessDetailsField.LegalName)}
        styling={InputText.Styling.Wide}
        title={translate('legal_name.title')}
        placeholder={
          isUserGBSoleTrader
            ? translate('legal_name.placeholder_sole_trader')
            : translate('legal_name.placeholder')
        }
        validation={renderValidation(validationMessage(NewBusinessDetailsField.LegalName))}
      />
      <InputText
        {...register(NewBusinessDetailsField.LegalCode, {
          required: true,
        })}
        styling={InputText.Styling.Wide}
        title={
          shouldUseCountrySpecificMessages ? getLegalCodeTitle() : translate('legal_code.title')
        }
        validation={
          renderErrorMessage(NewBusinessDetailsField.LegalCode) ||
          (isSiretNumberDisclaimerEnabled && translate('legal_code.hint_siret'))
        }
        placeholder={
          shouldUseCountrySpecificMessages
            ? getLegalCodePlaceholder()
            : translate('legal_code.placeholder')
        }
      />
      {!isUserGBSoleTrader && (
        <InputText
          {...register(NewBusinessDetailsField.RegistrarName, {
            required: true,
          })}
          styling={InputText.Styling.Wide}
          title={translate('registrar_name.title')}
          placeholder={translate('registrar_name.placeholder')}
          validation={renderValidation(validationMessage(NewBusinessDetailsField.RegistrarName))}
        />
      )}
      {!isVatFieldHidden && (
        <InputText
          {...register(NewBusinessDetailsField.Vat, {
            required: !isVatDisabled,
          })}
          styling={InputText.Styling.Wide}
          title={isCountryItaly ? getVatTitle() : translate('vat.title')}
          placeholder={translate('vat.placeholder')}
          validation={
            <>
              {renderErrorMessage(NewBusinessDetailsField.Vat)}
              {!isVatFieldMandatory && (
                <>
                  <Spacer size={Spacer.Size.XLarge} />
                  <Controller
                    name={NewBusinessDetailsField.IsVatDisabled}
                    render={({ field: { value, name, onChange } }) => (
                      <Checkbox
                        name={name}
                        text={translate('vat.check')}
                        checked={!!value}
                        onClick={handleIsVatDisabledChange}
                        onChange={event => onChange(event.target.checked)}
                        labelPosition="right"
                      />
                    )}
                    control={control}
                  />
                </>
              )}
            </>
          }
          disabled={!!isVatDisabled}
        />
      )}
    </SeparatedList>
  )
}

export default LegalEntitySection
